import { createApp, watch } from 'vue'
import * as vueGtag from 'vue-gtag'
import { clearAnalyticsUser, initializeAnalytics, syncAnalyticsUser, trackError } from './analytics'
import App from './App.vue'
// import './registerServiceWorker'
import router from './router'
import { store } from './store'
import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'
import { ThemeDefinition, createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'

import VueJsonPretty from 'vue-json-pretty'
// import { AVPlugin } from 'vue-audio-visual'
import 'vue-json-pretty/lib/styles.css'
import './global.css'

// VueFlow styles
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'
import '@vue-flow/minimap/dist/style.css'

// import { OhVueIcon, addIcons } from 'oh-vue-icons'
// import { FaLinux } from 'oh-vue-icons/icons'

// addIcons(FaLinux)

// Google Analytics Configuration
const gaEnabled = process.env.VUE_APP_GA_ENABLED === 'true'
const gaDebug = process.env.VUE_APP_GA_DEBUG === 'true'

// Configure vue-gtag
if (gaEnabled) {
  vueGtag.configure({
    tagId: process.env.VUE_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX',
    consentMode: 'denied',
    initMode: 'manual',
    config: {
      app_name: 'MissionSquad.ai',
      app_version: process.env.PACKAGE_VERSION || '1.0.0',
      debug_mode: gaDebug,
      send_page_view: false, // We'll handle this manually with router
      anonymize_ip: true,
      cookie_flags: 'SameSite=Strict;Secure',
      allow_ad_personalization_signals: false,
      // Custom dimensions
      custom_map: {
        'dimension1': 'user_type',
        'dimension2': 'theme_preference',
        'dimension3': 'authenticated'
      }
    }
  })
}

// const synthWave: ThemeDefinition = {
//   dark: true, // Consider setting this to true if you want a darker Synthwave theme
//   variables: {},
//   colors: {
//     background: '#35342d', // Dark purple for the background
//     'surface-variant': '#35342d', // Dark purple with transparent backgrounds
//     surface: '#35342d', // lighter purple for surfaces
//     primary: '#edde00', // Neon pink for primary elements
//     'primary-darken-1': '#d3b902', // A slightly darker shade of neon pink for primary darken
//     'primary-darken-2': '#d3b902',
//     'on-surface-variant': '#d3b902',
//     secondary: '#e5e5e5', // Bright cyan for secondary elements
//     'secondary-darken-1': '#b5b5b5', // A slightly darker shade of cyan for secondary darken
//     'secondary-darken-2': '#b5b5b5',
//     error: '#ff0033', // Bright red for errors
//     info: '#b5b5b5', // You can keep info color or adjust to fit the theme
//     success: '#0cea00', // Neon green for success messages
//     warning: '#fc9315', // Neon yellow for warnings
//     'on-primary': '#FFFFFF',
//     'on-secondary': '#000000',
//     'on-background': '#F0F0F0',
//     'on-surface': '#F0F0F0',
//     'border-color': '#64B5F6', // Using secondary blue or #af0982
//     'theme-code': '#2E1A47',
//     'theme-on-code': '#FF79C6', // Pinkish text or secondary
//     'terminal-background': '#0D1117',
//     'on-terminal-background': '#64B5F6',
//     'node-accent-1': '#00ffff',
//     'node-accent-2': '#ff6600',
//     'node-accent-3': '#39ff14',
//     'node-accent-4': '#ff0080',
//     'node-accent-5': '#8a2be2',
//     'tooltip-background': '#211135',
//     'tooltip-text': '#ff01b9'
//   }
// }

const dark: ThemeDefinition = {
  dark: true,
  colors: {
    background: '#141414',
    surface: '#1e1d18',
    'surface-variant': '#141414',
    primary: '#edde00',
    'primary-darken-1': '#d3b902',
    secondary: '#e5e5e5',
    'secondary-darken-1': '#edde00',
    error: '#ff0033',
    info: '#bca42a',
    success: '#e5e5e5',
    warning: '#fc9315',
    'on-primary': '#000000',
    'on-secondary': '#000000',
    'on-background': '#FFFFFF',
    'on-surface': '#FFFFFF',
    'border-color': '#35342d',
    'theme-code': '#262521',
    'theme-on-code': '#b5b5b5',
    'terminal-background': '#262521',
    'on-terminal-background': '#b5b5b5',
    'node-accent-1': '#E91E63',
    'node-accent-2': '#00BCD4',
    'node-accent-3': '#0cea00',
    'node-accent-4': '#fc9315',
    'node-accent-5': '#bca42a',
    'tooltip-background': '#292929',
    'tooltip-text': '#edde00',
    // Brand + marketing-surface tokens (theme-independent brand yellow; per-theme tints)
    'msq-yellow': '#edde00',
    'on-msq-yellow': '#141414',
    'msq-yellow-darken-1': '#d3b902',
    'accent-ink': '#edde00',
    'accent-green': '#2D8B4E',
    'surface-band': '#1e1d18',
    'surface-panel': '#1e1d18'
  }
}

const light: ThemeDefinition = {
  dark: false,
  colors: {
    background: '#e8e6d4',
    surface: '#e8e6d4',
    'surface-variant': '#e8e6d4',
    primary: '#1e1d18',
    'primary-darken-1': '#141414',
    secondary: '#1b1800',
    'secondary-darken-1': '#1b1800',
    error: '#e00000',
    info: '#bca42a',
    success: '#1b8900',
    warning: '#fc9315',
    'on-primary': '#FFFFFF',
    'on-secondary': '#141414',
    'on-background': '#141414',
    'on-surface': '#141414',
    'border-color': '#e8e6d4',
    'theme-code': '#E5E5E5',
    'theme-on-code': '#262521',
    'terminal-background': '#e8e6d4',
    'on-terminal-background': '#333333',
    'node-accent-1': '#8E24AA',
    'node-accent-2': '#00ACC1',
    'node-accent-3': '#0cea00',
    'node-accent-4': '#fc9315',
    'node-accent-5': '#5E35B1',
    'tooltip-background': '#262521',
    'tooltip-text': '#ffffff',
    // Brand + marketing-surface tokens (theme-independent brand yellow; per-theme tints)
    'msq-yellow': '#edde00',
    'on-msq-yellow': '#141414',
    'msq-yellow-darken-1': '#d3b902',
    'accent-ink': '#166534',
    'accent-green': '#2D8B4E',
    'surface-band': '#ddd9c0',
    'surface-panel': '#f4f1e4'
  }
}

const vuetify = createVuetify({
  theme: {
    defaultTheme: store.getters.currentTheme, // Use theme from store
    themes: {
      light,
      dark,
      // synthWave
    }
  },
  components: {
    ...components,
    VueJsonPretty
  },
  defaults: {
    input: {
      'data-1p-ignore': 'true'
    }
  },
  directives
})

const app = createApp(App).use(store).use(router).use(vuetify)

// Global error handler with analytics
app.config.errorHandler = (error: unknown, vm, info) => {
  console.error('Vue Error:', error, info)

  if (error instanceof Error) {
    void trackError(error, 'vue_error', false, {
      component_info: info,
      page_location: window.location.href
    })
  } else {
    void trackError(new Error(String(error)), 'vue_error', false, {
      component_info: info,
      page_location: window.location.href
    })
  }
}

function countItems(value: unknown): number {
  return Array.isArray(value) ? value.length : 0
}

function buildAnalyticsUserProperties() {
  return {
    username: store.getters.username as string,
    authenticated: Boolean(store.getters.authenticated),
    is_admin: Boolean(store.getters.isAdmin),
    can_publish_agents: Boolean(store.getters.canPublishAgents),
    theme_preference: store.getters.currentTheme as string,
    selected_tab: store.getters.selectedTab as string,
    provider_count: countItems(store.getters.providerConfigsList),
    model_count: countItems(store.getters.modelConfigsList),
    voice_count: countItems(store.getters.voiceConfigsList),
    agent_count: countItems(store.getters.agentConfigsList),
    shared_agent_count: countItems(store.getters.sharedAgents),
    workflow_count: countItems(store.getters.workflowList),
    enabled_mcp_server_count: countItems(store.getters.enabledServers),
    embedding_model_count: countItems(store.getters.embeddingModelConfigList),
    app_version: process.env.PACKAGE_VERSION || '1.0.0'
  }
}

// Watch for changes in the currentTheme in the store and update the Vuetify theme
watch(
  () => store.getters.currentTheme,
  (newTheme) => {
    vuetify.theme.global.name.value = newTheme
  }
)

watch(
  () => ({
    authenticated: Boolean(store.getters.authenticated),
    username: store.getters.username as string,
    isAdmin: Boolean(store.getters.isAdmin),
    canPublishAgents: Boolean(store.getters.canPublishAgents),
    currentTheme: store.getters.currentTheme as string,
    selectedTab: store.getters.selectedTab as string,
    providerCount: countItems(store.getters.providerConfigsList),
    modelCount: countItems(store.getters.modelConfigsList),
    voiceCount: countItems(store.getters.voiceConfigsList),
    agentCount: countItems(store.getters.agentConfigsList),
    sharedAgentCount: countItems(store.getters.sharedAgents),
    workflowCount: countItems(store.getters.workflowList),
    enabledMcpServerCount: countItems(store.getters.enabledServers),
    embeddingModelCount: countItems(store.getters.embeddingModelConfigList)
  }),
  (snapshot) => {
    if (!snapshot.authenticated || !snapshot.username) {
      void clearAnalyticsUser()
      return
    }

    void syncAnalyticsUser(snapshot.username, buildAnalyticsUserProperties())
  },
  { immediate: true }
)

await store.dispatch('fetchUserSettings')

await initializeAnalytics()

app.mount('#app')

export default vuetify
